import { alpha } from '@mui/system/colorManipulator';
import { getEarthDistance } from '#component/map/helper';
import { getContrastColor } from '#component/theme';
import { convertDistance } from '#helper';

const MIN_MAP_RADIUS = 0;
const MAX_MAP_RADIUS = 10000;

function clampRadius(radius) {
  if (!Number.isFinite(radius)) {
    return MIN_MAP_RADIUS;
  }
  return Math.min(MAX_MAP_RADIUS, Math.max(MIN_MAP_RADIUS, radius));
}

function getGeoJSON({ location, radius }) {
  const init = { lng: 8.496, lat: 50.121 };
  const { lng, lat } = location || init;
  const clampedRadius = clampRadius(radius);

  // https://stackoverflow.com/questions/7477003/calculating-new-longitude-latitude-from-old-n-meters
  const rEarth = 6371; // https://en.wikipedia.org/wiki/Mercator_projection
  const distLat = (clampedRadius / 1000 / rEarth) * (180 / Math.PI);
  const distLng = ((clampedRadius / 1000 / rEarth) * (180 / Math.PI)) /
    Math.cos((lat * Math.PI) / 180);

  const minLng = lng - distLng;
  const maxLng = lng + distLng;
  const minLat = lat - distLat;
  const maxLat = lat + distLat;

  const features = [];
  features.push(
    {
      type: 'Feature',
      properties: {
        radius: clampedRadius,
        lng,
        lat,
        center: true
      },
      geometry: {
        type: 'Point',
        coordinates: [lng, lat]
      }
    },
    {
      type: 'Feature',
      properties: {
        edge: true,
        cursor: 'ew-resize'
      },
      geometry: {
        type: 'Point',
        coordinates: [lng + distLng, lat]
      }
    },
    {
      type: 'Feature',
      properties: {
        edge: true,
        cursor: 'ew-resize'
      },
      geometry: {
        type: 'Point',
        coordinates: [lng - distLng, lat]
      }
    },
    {
      type: 'Feature',
      properties: {
        edge: true,
        cursor: 'ns-resize'
      },
      geometry: {
        type: 'Point',
        coordinates: [lng, lat + distLat]
      }
    },
    {
      type: 'Feature',
      properties: {
        edge: true,
        cursor: 'ns-resize'
      },
      geometry: {
        type: 'Point',
        coordinates: [lng, lat - distLat]
      }
    }
  );

  return {
    type: 'geojson',
    data: {
      type: 'FeatureCollection',
      features
    },
    locationsBound: [minLng, minLat, maxLng, maxLat]
  };
}

function onMoveCenter(e) {
  const coords = e.lngLat;
  // Set a UI indicator for dragging.
  this.getCanvas().style.cursor = 'grabbing';

  const data = {
    location: { lng: coords.lng, lat: coords.lat },
    radius: this.app.data.radius
  };
  const { locationsBound, ...geojson } = getGeoJSON(data);

  this.getSource('location').setData(geojson.data);
  this.app.data = data;
  // this.fitBounds(locationsBound, { padding: 50, maxZoom: this.getZoom() });
}

function onUpCenter() {
  this.getCanvas().style.cursor = '';
  // Unbind mouse/touch events
  this.off('mousemove', onMoveCenter);
  this.off('touchmove', onMoveCenter);
  this.app.helper.changeLocation([this.app.data.location]);
}

function onMoveEdge(e) {
  const coords = e.lngLat;
  const { location } = this.app.data;
  const r = getEarthDistance(coords.lng, coords.lat, location.lng, location.lat) * 1000;
  const data = {
    location,
    radius: clampRadius(r)
  };
  const { locationsBound, ...geojson } = getGeoJSON(data);

  this.getSource('location').setData(geojson.data);
  this.app.data = data;
}

function onUpEdge() {
  this.getCanvas().style.cursor = '';
  // Unbind mouse/touch events
  this.off('mousemove', onMoveEdge);
  this.off('touchmove', onMoveEdge);

  const { member, measurementSystem } = this.app.options;
  const { changeField } = this.app.helper;
  const fieldName = member ? `${member}.radius` : 'radius';
  const radius = Math.ceil(clampRadius(this.app.data.radius));
  this.app.data.radius = radius;
  changeField(fieldName, convertDistance(radius, 'metric', measurementSystem));
}

function addPlaceHover() {
  const { map } = this;

  map.on('mouseenter', 'place', () => {
    map.getCanvas().style.cursor = 'move';
  });
  map.on('mouseleave', 'place', () => {
    map.getCanvas().style.cursor = '';
  });

  map.on('mouseenter', 'place-radius', e => {
    const { cursor } = e.features[0].properties;
    map.getCanvas().style.cursor = cursor;
  });
  map.on('mouseleave', 'place-radius', () => {
    map.getCanvas().style.cursor = '';
  });
}

function addPlaceClick() {
  const { map } = this;
  map.on('mousedown', 'place', e => {
    // Prevent the default map drag behavior.
    e.preventDefault();

    map.getCanvas().style.cursor = 'grab';

    map.on('mousemove', onMoveCenter);
    map.once('mouseup', onUpCenter);
  });

  map.on('touchstart', 'place', e => {
    if (e.points.length !== 1) return;

    // Prevent the default map drag behavior.
    e.preventDefault();

    map.on('touchmove', onMoveCenter);
    map.once('touchend', onUpCenter);
  });

  map.on('mousedown', 'place-radius', e => {
    // Prevent the default map drag behavior.
    e.preventDefault();

    map.on('mousemove', onMoveEdge);
    map.once('mouseup', onUpEdge);
  });

  map.on('touchstart', 'place-radius', e => {
    if (e.points.length !== 1) return;

    // Prevent the default map drag behavior.
    e.preventDefault();

    map.on('touchmove', onMoveEdge);
    map.once('touchend', onUpEdge);
  });
}

/* function pixelValue(lat, meters, zoom) {
  const mapPixels = meters / (78271.484 / 2 ** zoom) / Math.cos((lat * Math.PI) / 180);
  const screenPixel = mapPixels * Math.floor(window.devicePixelRatio);
  return screenPixel;
} */

export default {
  addLayer(map) {
    this.map = map;

    map.addLayer({
      id: 'locations-radius',
      type: 'circle',
      source: 'location',
      filter: ['has', 'center'],
      paint: {
        'circle-color': getContrastColor(),
        'circle-radius': [
          'interpolate',
          ['exponential', 2],
          ['zoom'],
          0, 0,
          // https://stackoverflow.com/questions/37599561/drawing-a-circle-with-the-radius-in-miles-meters-with-mapbox-gl-js
          // 78271.484 / 2 ** zoom. For example: 78271.484 / 2 ** 20 = 0.07464550399780273
          // ['/', 78271.484, ['^', 2, 20]]
          // https://wiki.openstreetmap.org/wiki/Slippy_map_tilenames#Resolution_and_Scalehttps://wiki.openstreetmap.org/wiki/Slippy_map_tilenames#Resolution_and_Scale
          // 78271.484 meters/pixel * cos(latitude) / (2 ^ zoomlevel) = 0.0731462458862654
          // ['*', 78271.484, ['/', ['cos', ['get', 'lat']], ['^', 2, 20]]]
          20, ['/',
            ['/', ['get', 'radius'], ['/', 78271.484, ['^', 2, 20]]],
            ['cos', ['*', ['get', 'lat'], ['/', Math.PI, 180]]]
          ]],
        'circle-opacity': 0.5
      }
    });

    map.addLayer({
      id: 'place',
      // type: 'symbol',
      type: 'circle',
      source: 'location',
      filter: ['has', 'center'],
      paint: {
        // 'circle-color': ['concat', '#', ['get', 'color']],
        'circle-color': alpha(getContrastColor(), 0.5),
        'circle-radius': 5,
        'circle-stroke-width': 2,
        'circle-stroke-color': getContrastColor()
      }
      /* paint: {
        'icon-color': theme.palette.secondary.main,
        'icon-halo-color': theme.palette.secondary.main,
        'icon-halo-width': 2
      } */
    });

    map.addLayer({
      id: 'place-radius',
      // type: 'symbol',
      type: 'circle',
      source: 'location',
      filter: ['has', 'edge'],
      paint: {
        // 'circle-color': ['concat', '#', ['get', 'color']],
        'circle-color': getContrastColor(),
        'circle-radius': 5
      }
    });
  },
  getGeoJSON,
  // memoDrawClusters,
  addPlaceHover,
  addPlaceClick
};
